///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Dev-only window.__ebDebug hooks so tooling (LLM agents, browser
/// automation, manual console work) can read and set the camera without
/// simulating gestures. Documented for agents in
/// .cursor/rules/llm-debug-testing.mdc - keep that file in sync.
///

import { CameraParams } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import { type CameraDirector } from "@/earthbrowser/camera-director/CameraDirector"
import { Database } from "@/earthbrowser/database/Database"
import { EarthquakesDatasetLayer } from "@/earthbrowser/dataset-distribution/earthquakes/EarthquakesDatasetLayer"
import { HurricanesDatasetLayer } from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesDatasetLayer"
import { CloudForecastDatasetLayer } from "@/earthbrowser/dataset-distribution/cloud-forecast/CloudForecastDatasetLayer"
import { RadarForecastDatasetLayer } from "@/earthbrowser/dataset-distribution/radar-forecast/RadarForecastDatasetLayer"
import { SynopticDatasetLayer } from "@/earthbrowser/dataset-distribution/synoptic/SynopticDatasetLayer"
import { TextureSeries, textureLoadStats } from "@/earthbrowser/datasets/TextureSeries"
import { wgs } from "@/earthbrowser/utils/geographic"
import { getCityVignetteDebug, getCloudDetailProvider, getCrosshairStatsProvider, getSkyStatsProvider, getTileStatsProvider, getUserLocationStatsProvider, getWebcamStatsProvider } from "@/earthbrowser/utils/tileStatsRegistry"
import { getViewportInsets } from "@/earthbrowser/utils/viewportInsets"
import { appStore } from "@/redux/store"
import { isDevelopment } from "@/utils/environment"
import { copyRenderScene, exportRenderScene } from "@/utils/renderScene"
import { cameraLatLonDeg } from "@/utils/savedViews"
import { trig } from "@stovokor/math/math"
import { type Stovokor } from "@stovokor/Stovokor"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { analyticsDebugSnapshot, flushAnalytics } from "@/utils/analytics"
import { setRenderRequestTracer } from "@stovokor/engine/RenderRequest"
import { textureMemoryStats } from "@stovokor/shader/TextureMemory"
import { clearGpuBudgetOverride, hardBudgetBytes, isLiteMode, setGpuBudgetOverride, setLiteMode, softBudgetBytes, underMemoryPressure } from "@/earthbrowser/utils/MemoryBudget"
import { crashSentinelSnapshot, plantSimulatedCrash } from "@/earthbrowser/utils/CrashSentinel"
import { crashRecoverySnapshot } from "@/earthbrowser/utils/CrashRecovery"
import { memoryWatchSnapshot } from "@/earthbrowser/utils/MemoryWatch"

interface DebugCameraPose {
	lat: number
	lon: number
	/** Height above the surface in km; defaults to the current altitude */
	altitudeKm?: number
	/** 0 = straight down; defaults to the current tilt */
	tiltDeg?: number
	/** 0 = north up; defaults to the current bearing */
	bearingDeg?: number
}

/**
 * Install window.__ebDebug (development builds only). Called once at
 * engine startup with the live CameraDirector.
 */
export function installDebugHooks(cameraDirector: CameraDirector, context: StovokorContext, engine?: Stovokor): void {
	if (!isDevelopment() || typeof window === "undefined") return

	// Hidden automation tabs never get a requestAnimationFrame callback, so
	// the engine (and every focus flight) freezes. pumpFrames(true) swaps
	// the scheduler for a timer and restarts the loop; pumpFrames(false)
	// restores the real one
	const nativeRaf = window.requestAnimationFrame.bind(window)
	const nativeCancel = window.cancelAnimationFrame.bind(window)
	let pumping = false
	const pumpFrames = (on: boolean): boolean => {
		if (!engine || on === pumping) return pumping
		pumping = on
		if (on) {
			window.requestAnimationFrame = (cb: FrameRequestCallback): number => window.setTimeout(() => cb(performance.now()), 33)
			window.cancelAnimationFrame = (id: number): void => window.clearTimeout(id)
		} else {
			window.requestAnimationFrame = nativeRaf
			window.cancelAnimationFrame = nativeCancel
		}
		engine.pause()
		engine.run()
		return pumping
	}

	const getCamera = () => {
		const c = appStore.getState().earthbrowser.cameraState
		const { lat, lon } = cameraLatLonDeg(c.theta, c.phi)
		return {
			lat,
			lon,
			altitudeKm: c.radius - wgs.a,
			tiltDeg: c.tilt * trig.deg,
			bearingDeg: c.bearing * trig.deg,
			params: { ...c },
		}
	}

	const setCamera = (pose: DebugCameraPose) => {
		const current = appStore.getState().earthbrowser.cameraState
		const radius = pose.altitudeKm !== undefined ? wgs.a + pose.altitudeKm : current.radius
		const tilt = pose.tiltDeg !== undefined ? pose.tiltDeg * trig.rad : current.tilt
		const bearing = pose.bearingDeg !== undefined ? pose.bearingDeg * trig.rad : current.bearing
		cameraDirector.setPoseImmediate(new CameraParams(
			radius,
			pose.lon * trig.rad,
			(90 - pose.lat) * trig.rad,
			tilt,
			bearing
		))
		return getCamera()
	}

	;(window as unknown as Record<string, unknown>).__ebDebug = {
		getCamera,
		setCamera,
		/** Raw pose in engine units (radius km, angles in radians) */
		setCameraParams: (radius: number, theta: number, phi: number, tilt: number, bearing: number) => {
			cameraDirector.setPoseImmediate(new CameraParams(radius, theta, phi, tilt, bearing))
			return getCamera()
		},
		/** Live redux state (not the persisted localStorage snapshot) */
		getState: () => appStore.getState(),
		/** Dispatch a plain redux action, e.g. { type: "earthbrowser/sendDatasetAction", payload: {...} } */
		dispatch: (action: { type: string }) => appStore.dispatch(action),
		/** Currently rendered earth tiles: counts by level plus per-tile state */
		getTileStats: () => getTileStatsProvider()?.(),
		/** Sky ephemeris: sidereal time, sun/moon positions, moon phase, sun tint */
		getSky: () => getSkyStatsProvider()?.(),
		/** Crosshair: reticle / readout visibility and the camera motion source */
		getCrosshair: () => getCrosshairStatsProvider()?.(),
		/**
		 * Device location: { marker: { visible, x, y, accuracyPx, headingDeg,
		 * alpha }, following: "follow" | "heading" | null, state:
		 * dialog.userLocation }. Inject a fix without a GPS by dispatching
		 * dialog/updateUserLocation { status: "tracking", fix: { lat, lon,
		 * accuracyM, altitudeM: null, speedMps: null, courseDeg: null,
		 * timestamp } , headingDeg }
		 */
		getUserLocation: () => getUserLocationStatsProvider()?.(),
		/** Webcam cloud: cluster tier, transition blend, drawn domes/badges, materialized nodes */
		getWebcams: () => getWebcamStatsProvider()?.(),
		/** Cloud overlay detail region: active source (storm cutout / mosaic window), fade, window placement and tile load state */
		getCloudDetail: () => getCloudDetailProvider()?.(),
		/**
		 * Focused-city weather vignette: { visible, opacity, framingRadiusKm,
		 * weather (the CurrentWeather sample driving it), sunElevation, flash },
		 * or undefined while no city is focused
		 */
		getCityVignette: () => getCityVignetteDebug()?.snapshot(),
		/**
		 * Force conditions on the focused city's vignette to preview a look
		 * (partial CurrentWeather fields merge over the live sample), e.g.
		 * { weather_code: "thunderstorm", precip_rate_mmh: 6, cloud_cover_pct: 95 }
		 * or { weather_code: "snow", precip_rate_mmh: 2 }. null restores live data
		 */
		setCityVignetteWeather: (weather: Record<string, unknown> | null) => getCityVignetteDebug()?.override(weather),
		/**
		 * Texture time-series diagnostics (radar volumes, clouds, MRMS...):
		 * global atlas load stats plus per-series frame/display state, for
		 * debugging time-scrub and animation stalls
		 */
		/**
		 * Estimated GPU texture memory allocated through the engine (all
		 * Texture/TextureCube storage: dataset frames, earth tiles, labels...).
		 * The central counter for memory budgeting on devices with no heap
		 * APIs (iOS Safari)
		 */
		getGpuMemory: () => {
			const s = textureMemoryStats()
			return {
				...s,
				mb: Math.round(s.bytes / (1024 * 1024) * 10) / 10,
				textureMb: Math.round(s.textureBytes / (1024 * 1024) * 10) / 10,
				bufferMb: Math.round(s.bufferBytes / (1024 * 1024) * 10) / 10,
				peakMb: Math.round(s.peakBytes / (1024 * 1024) * 10) / 10,
				softBudgetMb: Math.round(softBudgetBytes() / (1024 * 1024)),
				hardBudgetMb: Math.round(hardBudgetBytes() / (1024 * 1024)),
				underPressure: underMemoryPressure(),
			}
		},
		/**
		 * Override the GPU memory budgets (MB) to exercise the enable gate and
		 * preload degradation on desktop; call with no args to clear
		 */
		setGpuBudgetMb: (softMb?: number, hardMb?: number) => {
			if (softMb === undefined || hardMb === undefined) {
				clearGpuBudgetOverride()
			} else {
				setGpuBudgetOverride(softMb * 1024 * 1024, hardMb * 1024 * 1024)
			}
		},
		/**
		 * Session lite mode (MemoryBudget): texture series run their minimum
		 * preload windows as if over the soft budget. CrashRecovery turns it
		 * on at level 2+; this toggles it by hand. Not persisted
		 */
		setLiteMode: (on: boolean) => setLiteMode(on),
		/**
		 * Page memory watch (MemoryWatch.ts, Chromium with cross-origin
		 * isolation): { supported, running, tripped, thresholdMb,
		 * constrained, lastMb, lastAt, samplesMb }. Readings over the
		 * threshold on a constrained device switch the session to lite mode
		 */
		getMemoryWatch: () => memoryWatchSnapshot(),
		/**
		 * Lose the WebGL context on purpose (WEBGL_lose_context). The
		 * ContextLossGuard reloads the page through crash recovery, so the
		 * next launch reports reason "webgl-context-lost" at level 1+.
		 * Returns false when the extension is unavailable
		 */
		loseContext: (): boolean => {
			const ext = context.gl.getExtension("WEBGL_lose_context")
			if (!ext) return false
			ext.loseContext()
			return true
		},
		/**
		 * Drawing buffer scale: the effective device pixels per CSS pixel the
		 * engine renders at, the display ratio, the cap, and the resulting
		 * backing store size
		 */
		getPixelRatio: () => ({
			pixelRatio: context.pixelRatio,
			devicePixelRatio: window.devicePixelRatio,
			maxPixelRatio: context.maxPixelRatio,
			drawingBuffer: [context.gl.drawingBufferWidth, context.gl.drawingBufferHeight],
		}),
		/**
		 * Chrome insets the UI published (CSS px the data dock covers) and the
		 * eased lens shift (NDC) the world camera currently applies, plus the
		 * screen point the view axis lands on. Center = visible-globe center
		 */
		getViewportInsets: () => {
			const projection = context.focusManager.getViewportHandler()?.camera.projection as
				{ offsetX?: number; offsetY?: number; width: number; height: number } | undefined
			const offsetX = projection?.offsetX ?? 0
			const offsetY = projection?.offsetY ?? 0
			return {
				insets: getViewportInsets(),
				offset: { x: offsetX, y: offsetY },
				viewAxisPx: projection
					? { x: (offsetX + 1) * projection.width / 2, y: (1 - offsetY) * projection.height / 2 }
					: undefined,
			}
		},
		/**
		 * Cap the render pixel ratio (takes effect next frame). Use 1 to see
		 * the pre-HiDPI look or to measure the fill-rate cost on a device
		 */
		setMaxPixelRatio: (ratio: number) => {
			context.maxPixelRatio = ratio
		},
		/**
		 * Crash sentinel state: the verdict on the previous session (level
		 * 0-3, heavy layers on at the kill, suspect, sameTab, loop
		 * history), the heavy layers on now (enable order), the layer whose
		 * staggered restore is in flight, the raw localStorage record
		 * (per-tab session entries, crash timestamps, pending restore),
		 * this session's breadcrumb trail (crumbs), and the trail recovered
		 * from the crashed session (recoveredTrail) - the last crumb
		 * brackets the allocation that killed the tab
		 */
		getCrashSentinel: () => crashSentinelSnapshot(),
		/**
		 * CrashRecovery progress: phase (idle | waiting | restoring |
		 * offered | done), level, suspect, queue / restored / skipped
		 * layers, lite mode, and whether the stability clock is armed or
		 * has fired (stableAt clears the loop history)
		 */
		getCrashRecovery: () => ({ ...crashRecoverySnapshot(), liteMode: isLiteMode() }),
		/**
		 * Plant a dead "alive" session so the NEXT reload detects a crash.
		 * Each reload that finds one raises the level inside the 30-minute
		 * loop window: plant + reload three times walks recovery through
		 * level 1 (silent staggered restore), 2 (lite mode, toast, suspect
		 * last) and 3 (start light, toast offers restore). `layers` are the
		 * heavy layers supposedly on at the kill; `restoring` names the
		 * layer whose restore was in flight (becomes the suspect); `sameTab`
		 * plants it under this tab's id (found by the same-tab rule, not
		 * staleness)
		 */
		simulateCrash: (layers: string[] = ["cloud"], options: { restoring?: string, sameTab?: boolean } = {}) => plantSimulatedCrash(layers, options),
		/**
		 * First-party analytics state (utils/analytics.ts): transport session
		 * id, queue depth, batches/events sent, the last 50 events, active
		 * seconds, enabled-layer dwell set and perf-sampling status
		 */
		getMetrics: () => analyticsDebugSnapshot(),
		/** Send any queued analytics events now (otherwise 15s / 20 events / pagehide) */
		flushMetrics: () => flushAnalytics(),
		/**
		 * Compile state of every shader program created so far: [{ id,
		 * available, errors }]. Programs compile lazily on first use, so
		 * render the view that uses a shader before checking it.
		 */
		/**
		 * Record who asks the engine to draw for `ms` milliseconds and resolve
		 * with the top call sites. Use to find what keeps an idle scene from
		 * dropping to the engine's idle frame rate (see Stovokor.idleFrameRate)
		 */
		traceRenderRequests: (ms: number = 2000): Promise<{ requestsPerSecond: number, sites: { count: number, site: string }[] }> => {
			const counts = new Map<string, number>()
			let total = 0
			setRenderRequestTracer(stack => {
				total++
				const site = stack.split("\n").slice(2, 6)
					.map(line => line.trim().replace(/^at /, "").replace(/\s*\(?https?:\/\/[^/]+\/(@fs\/)?/, " (").replace(/\?t=\d+/, "").replace(/:\d+:\d+\)?$/, ")"))
					.join(" <- ")
				counts.set(site, (counts.get(site) ?? 0) + 1)
			})
			return new Promise(resolve => setTimeout(() => {
				setRenderRequestTracer(undefined)
				const sites = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([site, count]) => ({ count, site }))
				resolve({ requestsPerSecond: Math.round(total * 1000 / ms), sites })
			}, ms))
		},
		getShaderErrors: () => context.shaderDiagnostics().filter(shader => shader.errors.length > 0),
		/** Every cached program; a program that is not available but reports
		 *  no errors failed to link with a message the parser dropped */
		getShaderPrograms: () => context.shaderDiagnostics(),
		getTextureSeriesStats: () => ({
			loads: {
				...textureLoadStats,
				avgMs: textureLoadStats.completed > 0
					? Math.round(textureLoadStats.totalMs / textureLoadStats.completed)
					: 0,
			},
			series: TextureSeries.debugSnapshots(),
		}),
		/** Layers waiting on data right now: [{ id, visible, phase, progress?,
		 *  detail?, atMs?, since }] (see datasets/LoadState.ts). Drives the
		 *  loading rings on the Layers button, rows, legend chips and the
		 *  timeline's lane shimmer; empty when every layer is current */
		getLoadStates: () => appStore.getState().datasetScene.layerManager?.getLoadStates() ?? [],
		/** Synoptic layer: displayed frame's centres (with track/tendency), fronts, jet */
		getSynoptic: () => SynopticDatasetLayer.debugSnapshot(),
		/** Forecast clouds (RRFS simulated IR): hand-off weight, observed cutoff, series (null when not registered) */
		getCloudForecast: () => {
			const layer = appStore.getState().datasetScene.layerManager?.getLayer("cloud_forecast")
			return layer instanceof CloudForecastDatasetLayer ? layer.debugSnapshot() : null
		},
		/** Forecast radar (RRFS / REFS): hand-off weight, observed cutoff, both series (null when not registered) */
		getRadarForecast: () => {
			const layer = appStore.getState().datasetScene.layerManager?.getLayer("radar_forecast")
			return layer instanceof RadarForecastDatasetLayer ? layer.debugSnapshot() : null
		},
		/** Earthquake cutout block: layout, sequence stats, terrain, focus (null when closed) */
		getQuakeCutout: () => EarthquakesDatasetLayer.debugSnapshot(),
		/** Storm column block: layout, wind model, IR frames and structure, focus (null when closed) */
		getStormColumn: () => HurricanesDatasetLayer.debugSnapshot(),
		/** Drive the frame loop from a timer when the tab gets no requestAnimationFrame (hidden automation tabs); returns the new state */
		pumpFrames,
		/**
		 * Author a video RenderScene (shared-types/render-scene.ts) from the
		 * live view: subject (focused ?target= or the camera fulcrum),
		 * look-at camera, visible layers, open cutout, clock window ending
		 * at the scene clock. Options: { spanHours, stepMinutes, style,
		 * format, appUrl }. copyRenderScene puts the JSON on the clipboard
		 */
		exportRenderScene,
		copyRenderScene,
		/**
		 * Run SQL against the local dataset SQLite database and return the rows.
		 * Throws instead of silently returning [] when the DB is unavailable or
		 * another tab holds the cross-tab lock (Database.exec no-ops in that
		 * state, which is indistinguishable from an empty result).
		 */
		query: async (sql: string, params?: unknown[]) => {
			const db = Database.getDb()
			if (!db) throw new Error("database not initialized")
			if (db.failed) throw new Error("database failed to load")
			if (!db.hasLock) throw new Error("another tab holds the database lock; close other EarthBrowser tabs and reload")
			return db.exec(sql, params, "high")
		},
	}
}
